import {
  axisFor,
  bucketSeries,
  buildSeries,
  summariseTrack,
  dayKey,
  describeQuantity,
  isMarkerKind,
  quantitativeRange,
  type Aggregate,
  type BucketSize,
  type Entry,
  type Track,
} from '@anytracker/core'
import { colourOfTrack } from '@anytracker/core'
import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../components/Button.js'
import { formatDate, formatDateTime } from '../format/dates.js'
import { ChartFrame, type FrameTick } from './ChartFrame.js'
import styles from './Charts.module.css'
import { categoryColour, paletteVar, trackColour } from './colours.js'
import {
  CHART,
  PLOT_BOTTOM,
  PLOT_HEIGHT,
  PLOT_RIGHT,
  countTicks,
  linePath,
  xOf,
  yOf,
  type Span,
} from './geometry.js'
import { StatisticsPanel } from './StatisticsPanel.js'

interface TrackChartProps {
  readonly track: Track
  readonly entries: readonly Entry[]
  readonly span: Span
  readonly size: BucketSize
  readonly aggregate: Aggregate
  readonly deviceZone: string
  /**
   * A control that chooses which track is drawn, shown where the title would
   * be (Insights). The report passes nothing and gets a plain title.
   */
  readonly picker?: ReactNode
}

/**
 * One track over time (T-403).
 *
 * The chart type follows the value type and cannot be overridden. A line
 * through booleans is meaningless; a line through unordered categories is worse
 * than meaningless, because it looks like it means something — the eye reads a
 * slope between `breakfast` and `dinner` that exists only because they were
 * listed in that order.
 *
 * Every number here comes from `@anytracker/core`. Nothing is computed in this
 * component, so the chart and the PDF report cannot disagree.
 */
export function TrackChart({
  track,
  entries,
  span,
  size,
  aggregate,
  deviceZone,
  picker,
}: TrackChartProps) {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<string | null>(null)

  const series = useMemo(
    () => buildSeries(track, entries, { fromEpochMs: span.from, toEpochMs: span.to }),
    [track, entries, span.from, span.to],
  )
  const bucketed = useMemo(
    () => bucketSeries(series, { size, aggregate }),
    [series, size, aggregate],
  )

  const timeTicks = useMemo(() => {
    const middle = span.from + (span.to - span.from) / 2
    return [span.from, middle, span.to].map((epochMs) => ({
      epochMs,
      label: formatDate({ epochMs, timeZone: deviceZone }),
    }))
  }, [span.from, span.to, deviceZone])

  const colour = trackColour(track)

  /*
   * The title, or the picker in its place. With a picker the heading is still
   * there for anyone navigating by headings, just not drawn twice.
   */
  const trackFill = paletteVar(colourOfTrack(track), 'fill')
  const title = (
    <h2 className={picker === undefined ? styles['title'] : 'visually-hidden'}>
      {picker === undefined && (
        <span className={styles['swatch']} style={{ background: trackFill }} />
      )}
      {track.name}
      {track.unit === undefined ? '' : ` (${track.unit})`}
    </h2>
  )

  if (bucketed.buckets.length === 0) {
    return (
      <section className={styles['card']} data-bleed>
        {picker}
        {title}
        <p className={styles['nothing']}>
          Nothing recorded for {track.name} in this range.
        </p>
      </section>
    )
  }

  const chart =
    bucketed.kind === 'categorical' ? (
      <CategoryMarks
        buckets={bucketed.buckets}
        span={span}
        onSelect={setSelected}
        selected={selected}
      />
    ) : track.kind.kind === 'boolean' ? (
      <RateMarks
        buckets={bucketed.buckets}
        span={span}
        colour={colour}
        onSelect={setSelected}
        selected={selected}
      />
    ) : (
      <LineMarks
        buckets={bucketed.buckets}
        span={span}
        colour={colour}
        range={quantitativeRange(track) ?? { min: 0, max: 1 }}
        onSelect={setSelected}
        selected={selected}
      />
    )

  const { valueRange, valueTicks, label } = describeAxis(track, bucketed)
  const summary = summariseTrack(track, series)
  const trendLabel = summary.kind === 'quantitative' ? summary.trendLabel : null
  const chosen = bucketed.buckets.find((bucket) => bucket.bucket.key === selected)

  /*
   * The range's own average, as a dashed rule across a line chart.
   *
   * Core's mean of every reading in the range, not of the plotted buckets, so
   * it is the same number the headline and the report state. Drawn in a
   * different hue *and* dashed, so it stays apart from the line in greyscale.
   */
  const isLine = bucketed.kind === 'quantitative' && track.kind.kind !== 'boolean'
  const average =
    isLine && summary.kind === 'quantitative' ? summary.statistics.mean : null
  const averageColour = paletteVar(
    colourOfTrack(track) === 'clay' ? 'cornflower' : 'clay',
    'deep',
  )
  const averageRule =
    average === null ? null : (
      <line
        x1={CHART.padLeft}
        x2={PLOT_RIGHT}
        y1={yOf(average, valueRange)}
        y2={yOf(average, valueRange)}
        stroke={averageColour}
        className={styles['average']}
      />
    )

  return (
    <section className={styles['card']} data-bleed>
      <div className={styles['head']}>
        <div className={styles['headMain']}>
          {picker}
          {title}
          <span className={styles['kindNote']}>{chartNote(track, aggregate, size)}</span>
          {/*
            The trend, in core's own words. `summariseTrack` builds the same row
            the printed report prints, so the pill and the document cannot phrase
            the same fact differently — and a chart that only *looks* like it is
            going up is the thing this sentence exists to check.
          */}
          {trendLabel !== null && (
            <span className={styles['trendPill']} data-testid="trend-pill">
              {trendLabel}
            </span>
          )}
        </div>
        {/*
          The headline number, in core's words: the same label the statistics
          below and the printed report use.
        */}
        <div className={styles['headline']}>
          <span className={styles['headlineValue']}>
            {summary.kind === 'quantitative'
              ? summary.meanLabel
              : String(summary.kind === 'categorical' ? summary.count : 0)}
          </span>
          <span className={styles['headlineLabel']}>
            {summary.kind === 'quantitative'
              ? 'average'
              : summary.kind === 'categorical' && summary.count === 1
                ? 'time'
                : 'times'}
          </span>
        </div>
      </div>

      <ChartFrame
        label={label}
        valueRange={valueRange}
        valueTicks={valueTicks}
        span={span}
        timeTicks={timeTicks}
      >
        {averageRule}
        {chart}
      </ChartFrame>

      {/* A line sample each, so the dash carries the difference as well as the hue. */}
      {isLine && (
        <ul className={styles['legend']}>
          <li>
            <svg
              width="14"
              height="4"
              className={styles['swatchLine']}
              aria-hidden="true"
            >
              <line x1="0" y1="2" x2="14" y2="2" stroke={colour} strokeWidth="2" />
            </svg>
            {chartNote(track, aggregate, size).split(' · ')[0]}
          </li>
          {average !== null && (
            <li>
              <svg
                width="14"
                height="4"
                className={styles['swatchLine']}
                aria-hidden="true"
              >
                <line
                  x1="0"
                  y1="2"
                  x2="14"
                  y2="2"
                  stroke={averageColour}
                  strokeWidth="1.5"
                  strokeDasharray="3 2"
                />
              </svg>
              Average for the range
            </li>
          )}
        </ul>
      )}

      {series.unresolved > 0 && (
        <p className={styles['warning']}>
          {series.unresolved === 1
            ? '1 measurement could not be read against this track as it is now.'
            : `${String(series.unresolved)} measurements could not be read against this track as it is now.`}
        </p>
      )}

      {chosen !== undefined && (
        <div className={styles['selection']}>
          <div className={styles['selectionHead']}>
            <strong>{chosen.bucket.key}</strong>
            <Button
              onClick={() => {
                const from = dayKey({
                  epochMs: chosen.bucket.startEpochMs,
                  timeZone: chosen.bucket.timeZone,
                })
                const to = dayKey({
                  epochMs: chosen.bucket.endEpochMs - 1,
                  timeZone: chosen.bucket.timeZone,
                })
                void navigate(`/timeline?track=${track.id}&from=${from}&to=${to}`)
              }}
            >
              Show in timeline
            </Button>
          </div>
          <ul className={styles['selectionList']}>
            {chosen.points.map((point) => (
              <li key={point.entryId}>
                <span>{formatDateTime(point.at)}</span>
                <strong>{point.label}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        The accessible fallback the ticket requires, and the honest one: the
        chart is `role="img"`, so this is the only way to reach the values with
        a screen reader — and it is also the fastest way for anyone to read an
        exact number off a chart.
      */}
      <details className={styles['tableWrap']}>
        <summary>Show the numbers</summary>
        <table className={styles['table']}>
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">{bucketed.kind === 'categorical' ? 'Counts' : 'Value'}</th>
              <th scope="col">Entries</th>
            </tr>
          </thead>
          <tbody>
            {bucketed.kind === 'categorical'
              ? bucketed.buckets.map((bucket) => (
                  <tr key={bucket.bucket.key}>
                    <th scope="row">{bucket.bucket.key}</th>
                    <td>
                      {bucket.counts
                        .map((count) => `${count.label} ${String(count.count)}`)
                        .join(', ')}
                    </td>
                    <td>{bucket.total}</td>
                  </tr>
                ))
              : bucketed.buckets.map((bucket) => (
                  <tr key={bucket.bucket.key}>
                    <th scope="row">{bucket.bucket.key}</th>
                    <td>{describeQuantity(track, bucket.value)}</td>
                    <td>{bucket.count}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </details>

      {/* Last, and flush with the band's foot, so the strip reads as the
          design's second band joined under the chart. */}
      <StatisticsPanel series={series} track={track} />
    </section>
  )
}

// ─── Axis description ─────────────────────────────────────────────────────────

type Bucketed = ReturnType<typeof bucketSeries>

function describeAxis(
  track: Track,
  bucketed: Bucketed,
): { valueRange: { min: number; max: number }; valueTicks: FrameTick[]; label: string } {
  if (bucketed.kind === 'categorical') {
    const max = Math.max(...bucketed.buckets.map((bucket) => bucket.total), 1)
    const ticks = countTicks(max)
    return {
      valueRange: { min: 0, max: ticks[ticks.length - 1] ?? max },
      valueTicks: ticks.map((quantity) => ({ quantity, label: String(quantity) })),
      // A marker has no options, so "counts of each option" describes something
      // that is not on the chart — and this string is the whole chart to anyone
      // reading it with a screen reader.
      label: isMarkerKind(track.kind)
        ? `${track.name}: times recorded per period.`
        : `${track.name}: counts of each option per period.`,
    }
  }

  if (track.kind.kind === 'boolean') {
    // A rate axis, not the track's own 0/1: with several entries in a period the
    // interesting number is how often, and 0/1 labels would be a lie about a
    // bar sitting at 0.6.
    return {
      valueRange: { min: 0, max: 1 },
      valueTicks: [
        { quantity: 0, label: '0%' },
        { quantity: 0.5, label: '50%' },
        { quantity: 1, label: '100%' },
      ],
      label: `${track.name}: how often per period.`,
    }
  }

  const axis = axisFor(track)
  if (axis === null) {
    return {
      valueRange: { min: 0, max: 1 },
      valueTicks: [],
      label: `${track.name} over time.`,
    }
  }
  return {
    valueRange: { min: axis.min, max: axis.max },
    // An ordered enum's ticks are its option labels, never the scores behind
    // them — the scores are an implementation detail nobody chose.
    valueTicks: [...axis.ticks],
    label: `${track.name} over time.`,
  }
}

/** What one plotted point is, in words: "Average per day · 0 to 10". */
function chartNote(track: Track, aggregate: Aggregate, size: BucketSize): string {
  const per = `per ${size}`
  const scale = quantitativeRange(track)
  const bounds =
    track.kind.kind === 'numeric' && scale !== null
      ? ` · ${String(scale.min)} to ${String(scale.max)}`
      : ''
  switch (track.kind.kind) {
    case 'boolean':
      return `Rate ${per}`
    case 'enum':
      return track.kind.ordered ? `${aggregateWord(aggregate)} ${per}` : `Counts ${per}`
    // A marker has no magnitude, so the only thing to draw is how often.
    case 'marker':
      return `Times ${per}`
    // Counts of each option, exactly as an unordered enum — one entry simply
    // contributes to several of them.
    case 'multi':
      return `Counts ${per}`
    case 'numeric':
      return `${aggregateWord(aggregate)} ${per}${bounds}`
  }
}

function aggregateWord(aggregate: Aggregate): string {
  switch (aggregate) {
    case 'mean':
      return 'Average'
    case 'median':
      return 'Median'
    case 'min':
      return 'Lowest'
    case 'max':
      return 'Highest'
    case 'count':
      return 'Count'
    case 'sum':
      return 'Total'
  }
}

// ─── Marks ────────────────────────────────────────────────────────────────────

interface MarkProps<B> {
  readonly buckets: readonly B[]
  readonly span: Span
  readonly selected: string | null
  readonly onSelect: (key: string | null) => void
}

type QuantitativeBuckets = Extract<Bucketed, { kind: 'quantitative' }>['buckets']
type CategoricalBuckets = Extract<Bucketed, { kind: 'categorical' }>['buckets']

function LineMarks({
  buckets,
  span,
  colour,
  range,
  selected,
  onSelect,
}: MarkProps<QuantitativeBuckets[number]> & {
  readonly colour: string
  readonly range: { min: number; max: number }
}) {
  const points = buckets.map((bucket) => ({
    x: xOf(bucket.bucket.startEpochMs, span),
    y: yOf(bucket.value, range),
    followsGap: bucket.followsGap,
    key: bucket.bucket.key,
  }))
  const latest = points[points.length - 1]

  return (
    <>
      <path d={linePath(points)} fill="none" stroke={colour} className={styles['line']} />

      {/*
        Every point is still tappable, and none of them is drawn.
        
        A dot per reading is a scatter plot wearing a line, and at thirty of them
        they merge into a dotted rope. These are transparent hit targets at 11px
        — comfortably bigger than the 3px dots they replace, so selecting a
        bucket got easier rather than harder.
      */}
      {/*
        The latest reading, which is the one people look for, and the one that
        has been tapped: a hollow square on the line, filled when chosen.
      */}
      {latest !== undefined && selected !== latest.key && (
        <rect
          x={latest.x - 4}
          y={latest.y - 4}
          width={8}
          height={8}
          stroke={colour}
          className={styles['latest']}
        />
      )}
      {points
        .filter((point) => point.key === selected)
        .map((point) => (
          <rect
            key={`chosen-${point.key}`}
            x={point.x - 5}
            y={point.y - 5}
            width={10}
            height={10}
            fill={colour}
            className={styles['point']}
          />
        ))}

      {points.map((point) => (
        <circle
          key={point.key}
          cx={point.x}
          cy={point.y}
          r={11}
          className={styles['hit']}
          onClick={() => {
            onSelect(selected === point.key ? null : point.key)
          }}
        >
          <title>{point.key}</title>
        </circle>
      ))}
    </>
  )
}

function RateMarks({
  buckets,
  span,
  colour,
  selected,
  onSelect,
}: MarkProps<QuantitativeBuckets[number]> & { readonly colour: string }) {
  return (
    <>
      {buckets.map((bucket) => {
        const left = xOf(bucket.bucket.startEpochMs, span)
        const right = xOf(bucket.bucket.endEpochMs, span)
        const width = Math.max(right - left - 1, 1.5)
        const y = yOf(bucket.value, { min: 0, max: 1 })
        return (
          <rect
            key={bucket.bucket.key}
            x={left}
            y={y}
            width={width}
            height={Math.max(PLOT_BOTTOM - y, 0)}
            fill={colour}
            opacity={selected === bucket.bucket.key ? 1 : 0.85}
            className={styles['bar']}
            onClick={() => {
              onSelect(selected === bucket.bucket.key ? null : bucket.bucket.key)
            }}
          >
            <title>{bucket.bucket.key}</title>
          </rect>
        )
      })}
    </>
  )
}

function CategoryMarks({
  buckets,
  span,
  selected,
  onSelect,
}: MarkProps<CategoricalBuckets[number]>) {
  const max = Math.max(...buckets.map((bucket) => bucket.total), 1)
  const ticks = countTicks(max)
  const top = ticks[ticks.length - 1] ?? max
  const optionOrder = [
    ...new Set(buckets.flatMap((bucket) => bucket.counts.map((count) => count.optionId))),
  ]

  return (
    <>
      {buckets.map((bucket) => {
        const left = xOf(bucket.bucket.startEpochMs, span)
        const right = xOf(bucket.bucket.endEpochMs, span)
        const width = Math.max(right - left - 1, 1.5)
        let stacked = 0
        return (
          <g
            key={bucket.bucket.key}
            onClick={() => {
              onSelect(selected === bucket.bucket.key ? null : bucket.bucket.key)
            }}
          >
            {[...bucket.counts]
              .sort(
                (a, b) =>
                  optionOrder.indexOf(a.optionId) - optionOrder.indexOf(b.optionId),
              )
              .map((count) => {
                const height = (count.count / top) * PLOT_HEIGHT
                stacked += height
                return (
                  <rect
                    key={count.optionId}
                    x={left}
                    y={CHART.padTop + PLOT_HEIGHT - stacked}
                    width={width}
                    height={Math.max(height, 0)}
                    fill={categoryColour(optionOrder.indexOf(count.optionId))}
                    opacity={selected === bucket.bucket.key ? 1 : 0.85}
                    className={styles['bar']}
                  >
                    <title>{`${count.label}: ${String(count.count)}`}</title>
                  </rect>
                )
              })}
          </g>
        )
      })}
    </>
  )
}
